'use client';

import React from 'react';
import DatePicker from 'react-datepicker';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { cn } from '@/lib/utils';
import 'react-datepicker/dist/react-datepicker.css';

interface FormDatePickerProps<T extends FieldValues> {
  label: string;
  name: Path<T>;
  control: Control<T>;
  error?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  timeMode?: 'start' | 'end'; // 'start' = 00:00:00, 'end' = 23:59:59
  showYearDropdown?: boolean; // Enable year dropdown for DOB fields
  maxDate?: Date; // Maximum selectable date
  minDate?: Date; // Minimum selectable date
}

export function FormDatePicker<T extends FieldValues>({
  label,
  name,
  control,
  error,
  className,
  placeholder = 'Select date',
  disabled = false,
  timeMode = 'start',
  showYearDropdown = false,
  maxDate,
  minDate,
}: FormDatePickerProps<T>) {
  return (
    <div className={cn('w-full', className)}>
      <label className="mb-1.5 block text-sm font-medium text-gray-700">
        {label}
      </label>

      <div className="relative">
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              selected={value}
              onChange={(date) => {
                if (date) {
                  // Set time based on timeMode
                  if (timeMode === 'end') {
                    // Set to end of day (23:59:59)
                    date.setHours(23, 59, 59, 999);
                  } else {
                    // Set to start of day (00:00:00)
                    date.setHours(0, 0, 0, 0);
                  }
                }
                onChange(date);
              }}
              dateFormat="MMMM d, yyyy"
              placeholderText={placeholder}
              disabled={disabled}
              showYearDropdown={showYearDropdown}
              scrollableYearDropdown={showYearDropdown}
              yearDropdownItemNumber={showYearDropdown ? 100 : undefined}
              maxDate={maxDate}
              minDate={minDate}
              autoComplete="bday"
              className={cn(
                'form-input block w-full rounded-lg border-gray-300 pl-20 shadow-sm transition-all duration-200',
                'text-sm focus:border-primary-500 focus:ring-primary-500',
                'bg-white/100 hover:bg-white focus:bg-white',
                error &&
                  'border-red-300 focus:border-red-500 focus:ring-red-500',
                disabled && 'cursor-not-allowed bg-gray-100 opacity-75'
              )}
              wrapperClassName="w-full"
              popperClassName="!z-[99999]"
              popperPlacement="bottom-start"
              showPopperArrow={false}
              portalId="root"
              onCalendarOpen={() => {
                // Prevent modal from closing when calendar opens
                document.body.style.pointerEvents = 'auto';
              }}
              onCalendarClose={() => {
                // Restore normal pointer events when calendar closes
                document.body.style.pointerEvents = 'auto';
              }}
            />
          )}
        />
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
